import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import BlockIcon from '@mui/icons-material/Block';
import {
  creedApiBaseUrl,
  keysApi,
  type ApiKey,
  type KeyUsage,
  type RotatedKey,
} from '../lib/api';
import { useUserPermissions } from '../hooks/useUserPermissions';

export default function ApiKeys() {
  const { canWriteApi } = useUserPermissions();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<{ rawKey: string; label: string; keyPrefix: string } | null>(null);

  const [usage, setUsage] = useState<KeyUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuKey, setMenuKey] = useState<ApiKey | null>(null);

  const load = () =>
    keysApi
      .list()
      .then(setKeys)
      .catch(() => setError('Failed to load API keys'))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      const result = await keysApi.create(newLabel.trim());
      setRevealedKey(result);
      setNewLabel('');
      load();
    } catch {
      setError('Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (key: ApiKey) => {
    try {
      const updated = await keysApi.update(key._id, { active: !key.active });
      setKeys((prev) => prev.map((k) => (k._id === key._id ? updated : k)));
    } catch {
      setError('Failed to update key');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Permanently delete this API key? This cannot be undone.')) return;
    try {
      await keysApi.remove(id);
      setKeys((prev) => prev.filter((k) => k._id !== id));
    } catch {
      setError('Failed to delete key');
    }
  };

  const handleRotate = async (key: ApiKey) => {
    if (!confirm(`Rotate ${key.label}? The previous key will stop working immediately.`)) return;
    try {
      const rotated: RotatedKey = await keysApi.rotate(key._id);
      setRevealedKey({ rawKey: rotated.rawKey, label: rotated.label, keyPrefix: rotated.keyPrefix });
      load();
    } catch {
      setError('Failed to rotate key');
    }
  };

  const handleRevoke = async (key: ApiKey) => {
    if (!confirm(`Revoke ${key.label}? It will stop accepting requests until you re-enable it.`)) return;
    try {
      const updated = await keysApi.revoke(key._id);
      setKeys((prev) => prev.map((k) => (k._id === key._id ? updated : k)));
    } catch {
      setError('Failed to revoke key');
    }
  };

  const openUsage = async (key: ApiKey) => {
    setUsage(null);
    setUsageLoading(true);
    try {
      const u = await keysApi.usage(key._id);
      setUsage(u);
    } catch {
      setError('Failed to load usage');
    } finally {
      setUsageLoading(false);
    }
  };

  if (loading) return <CircularProgress />;

  const apiUrl = creedApiBaseUrl;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          API Keys
        </Typography>
        {canWriteApi && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New Key
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper
        variant="outlined"
        sx={{ mb: 2, px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.50' }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          API URL
        </Typography>
        <code style={{ flex: 1, fontSize: 13, wordBreak: 'break-all' }}>
          {apiUrl || '—'}
        </code>
        {apiUrl && (
          <Tooltip title="Copy">
            <IconButton size="small" onClick={() => navigator.clipboard.writeText(apiUrl)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Paper>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Prefix</TableCell>
              <TableCell align="right">Requests</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No API keys yet</TableCell>
              </TableRow>
            ) : (
              keys.map((key) => (
                <TableRow key={key._id} hover>
                  <TableCell>{key.label}</TableCell>
                  <TableCell>
                    <code style={{ fontSize: 12 }}>{key.keyPrefix}…</code>
                  </TableCell>
                  <TableCell align="right">{key.requestCount.toLocaleString()}</TableCell>
                  <TableCell>
                    {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>{new Date(key.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={canWriteApi ? '' : 'Read-only — requires api:write'}>
                      <span>
                        <Switch
                          checked={key.active}
                          size="small"
                          disabled={!canWriteApi}
                          onChange={() => handleToggleActive(key)}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View usage">
                      <IconButton size="small" onClick={() => openUsage(key)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canWriteApi && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuKey(key);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuKey(null); }}
      >
        <MenuItem
          onClick={() => {
            if (menuKey) handleRotate(menuKey);
            setMenuAnchor(null);
          }}
        >
          <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
          Rotate
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuKey) handleRevoke(menuKey);
            setMenuAnchor(null);
          }}
        >
          <BlockIcon fontSize="small" sx={{ mr: 1 }} />
          Revoke (soft-delete)
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuKey) handleDelete(menuKey._id);
            setMenuAnchor(null);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Hard delete
        </MenuItem>
      </Menu>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Label"
            placeholder="e.g. CRM Integration"
            fullWidth
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !newLabel.trim()}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Raw key reveal dialog (creation OR rotation) */}
      <Dialog
        open={!!revealedKey}
        onClose={() => { setRevealedKey(null); setCreateOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now — it will not be shown again.
          </Alert>
          <Box display="flex" alignItems="center" gap={1}>
            <code
              style={{
                flex: 1,
                background: '#f4f4f4',
                padding: '8px 12px',
                borderRadius: 4,
                wordBreak: 'break-all',
                fontSize: 13,
              }}
            >
              {revealedKey?.rawKey}
            </code>
            <Tooltip title="Copy">
              <IconButton onClick={() => navigator.clipboard.writeText(revealedKey?.rawKey ?? '')}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box mt={2} display="flex" gap={1} flexWrap="wrap">
            <Chip label={`Label: ${revealedKey?.label}`} size="small" />
            <Chip label={`Prefix: ${revealedKey?.keyPrefix}…`} size="small" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => { setRevealedKey(null); setCreateOpen(false); }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usage drill-down */}
      <Dialog
        open={usageLoading || !!usage}
        onClose={() => { setUsage(null); setUsageLoading(false); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{usage?.key?.label ?? 'Loading…'}</DialogTitle>
        <DialogContent>
          {usageLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : usage ? (
            <>
              <Typography variant="body2" color="text.secondary" mb={2}>
                <strong>{usage.key.requestCount.toLocaleString()}</strong> total requests · last used{' '}
                {usage.key.lastUsedAt ? new Date(usage.key.lastUsedAt).toLocaleString() : 'never'}
              </Typography>

              <Typography variant="subtitle2" fontWeight={700} mt={2} mb={1}>
                By entity type
              </Typography>
              <Paper variant="outlined">
                <Table size="small">
                  <TableBody>
                    {usage.byEntity.length === 0 ? (
                      <TableRow><TableCell>No data</TableCell></TableRow>
                    ) : (
                      usage.byEntity.map((row) => (
                        <TableRow key={row._id}>
                          <TableCell>{row._id ?? '—'}</TableCell>
                          <TableCell align="right">{row.requests.toLocaleString()}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>

              <Typography variant="subtitle2" fontWeight={700} mt={3} mb={1}>
                Top states
              </Typography>
              <Paper variant="outlined">
                <Table size="small">
                  <TableBody>
                    {usage.byState.length === 0 ? (
                      <TableRow><TableCell>No data</TableCell></TableRow>
                    ) : (
                      usage.byState.map((row) => (
                        <TableRow key={row._id}>
                          <TableCell>{row._id}</TableCell>
                          <TableCell align="right">{row.requests.toLocaleString()}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>

              <Typography variant="subtitle2" fontWeight={700} mt={3} mb={1}>
                Recent activity
              </Typography>
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Entity</TableCell>
                      <TableCell align="right">Filings</TableCell>
                      <TableCell align="right">Time (ms)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usage.recent.length === 0 ? (
                      <TableRow><TableCell colSpan={4}>No data</TableCell></TableRow>
                    ) : (
                      usage.recent.map((a) => (
                        <TableRow key={a._id}>
                          <TableCell>{new Date(a.requestedAt).toLocaleString()}</TableCell>
                          <TableCell>{a.params.entity}</TableCell>
                          <TableCell align="right">{a.resultSummary?.total ?? '—'}</TableCell>
                          <TableCell align="right">{a.responseTimeMs ?? '—'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUsage(null); setUsageLoading(false); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
